import React, { useState } from "react";
import styled from "styled-components";
import {
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { Delete } from "@mui/icons-material";
import { StyledErrorMessage } from "../style/modalStyle";
import { StyledLink } from "../style/ititn/itinUpload";
import PassportInstructionModal from "../itin/PassportInstructionModal";
import { EyeIcon } from "../../assets/Icon/dashboredIcon";

interface FilePropType {
  title?: string;
  icon?: React.ReactNode;
  desc?: string;
  name?: string;
  formik?: any;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filePreview: string | null;
  setFilePreview: React.Dispatch<React.SetStateAction<string | null>>;
  setFileUrl?: (data: any) => void;
  setOpenPreview?: (data: any) => void;
  setPdfUrl?: (data: any) => void;
  setPdfOpen?: (data: any) => void;
  disabled?: boolean;
  isEyeIcon?: boolean;
  accept?: string;
  showInstruction?: boolean;
  placeholder?: string;
  isOptional?: boolean;
  wrapperPadding?: string;
}
const data = [
  {
    instructions: [
      "All four corners of the full passport should be visible. Both the top half and bottom half of the passport should included in the picture.",
      "The photo should be in color and not blurry.",
      "The passport should be valid least 6 months and not expired.",
      "Please scan your passport with the scanner.",
    ],
  },
];
const UploadFileFieldNew = (props: FilePropType) => {
  const {
    title,
    icon,
    name,
    formik,
    handleFileChange,
    setFilePreview,
    setFileUrl,
    setOpenPreview,
    setPdfUrl,
    setPdfOpen,
    filePreview,
    isEyeIcon,
    disabled,
    accept,
    wrapperPadding,
    showInstruction,
    placeholder,
    isOptional,
  } = props;

  const fieldName = name || "file-upload";
  const error = formik?.touched[fieldName] && formik?.errors[fieldName];
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const parsedAccept = accept
    ? accept.split(",").reduce((acc: any, type) => {
        const trimmed = type.trim();
        if (!acc[trimmed]) acc[trimmed] = [];
        return acc;
      }, {})
    : {
        "image/jpeg": [".jpeg", ".jpg"],
        "image/png": [".png"],
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          [".docx"],
      };

  const { getRootProps, getInputProps } = useDropzone({
    accept: parsedAccept,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setUploadError("Invalid file type.");
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const previewURL = URL.createObjectURL(file);
        formik.setFieldValue(fieldName, file);
        setFilePreview(file.name);
        setFileUrl?.(previewURL);
        setUploadError(null);
      }
    },
  });

  const removeFile = () => {
    formik.setFieldValue(fieldName, null);
    setFilePreview(null);
  };

  return (
    <>
      <Wrapper
        wrapperPadding={wrapperPadding}
        style={{
          border: error ? "1px solid red" : "1px solid transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid transparent",
          }}
        >
          <Title variant="h4">
            {title}{" "}
            {isOptional && (
              <span
                style={{ color: "#666", fontSize: "12px", marginLeft: "10px" }}
              >
                optional
              </span>
            )}
          </Title>
          {showInstruction && (
            <StyledLink to="" onClick={() => setIsModalOpen(true)}>
              Passport Instruction
            </StyledLink>
          )}
        </div>
        <DropArea {...getRootProps()}>
          <input {...getInputProps()} disabled={disabled} />
          <UploadContainer>
            {icon}
            <StyledTypography>
              {placeholder !== "" ? (
                placeholder
              ) : title !== "Scan Passport" ? (
                <>
                  <span>Click here</span> or drag & drop your file
                </>
              ) : (
                "Scan your passport"
              )}
            </StyledTypography>
          </UploadContainer>
        </DropArea>

        {filePreview && (
          <PreviewContainer>
            <Typography
              sx={{
                overflowX: "auto",
                width: isTablet ? "300px" : isMobile ? "150px" : "100%",
              }}
            >
              {filePreview}
            </Typography>

            <Typography
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {isEyeIcon && (
                <button
                  type="button"
                  style={{
                    background: "none",
                    border: "none",
                    paddingTop: "5px",
                  }}
                  onClick={() => {
                    const uploadedFile = formik.values[fieldName];
                    if (!uploadedFile) return;

                    // If PDF → open PDF modal
                    if (
                      uploadedFile.type === "application/pdf" ||
                      uploadedFile.name.toLowerCase().endsWith(".pdf")
                    ) {
                      const pdfURL = URL.createObjectURL(uploadedFile);
                      setPdfUrl?.(pdfURL);
                      setPdfOpen?.(true);
                    } else {
                      // Otherwise → open image preview modal
                      const imgURL = URL.createObjectURL(uploadedFile);
                      setFileUrl?.(imgURL);
                      setOpenPreview?.(true);
                    }
                  }}
                  disabled={disabled}
                >
                  <EyeIcon />
                </button>
              )}
              <IconButton onClick={removeFile} disabled={disabled}>
                <Delete color={disabled ? "disabled" : "error"} />
              </IconButton>
            </Typography>
          </PreviewContainer>
        )}
      </Wrapper>

      {uploadError || error ? (
        <StyledErrorMessage>{uploadError || error}</StyledErrorMessage>
      ) : (
        <></>
      )}

      <PassportInstructionModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Passport Upload Instruction"
        data={data}
      />
    </>
  );
};

export default UploadFileFieldNew;

const Wrapper = styled.div<{
  wrapperPadding?: string;
}>`
  background: white;
  padding: ${(props) => props.wrapperPadding || "24px"};
  border-radius: 10px;
  box-shadow: 0px 2px 40px 0px #dde3ea33;
`;
const Title = styled(Typography)`
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
`;
const DropArea = styled.div`
  margin-top: 10px;
  border: 0.6px dashed #2977cc;
  border-radius: 10px;
  padding: 40px;
  background: #eaf1fa;
  text-align: center;
  cursor: pointer;
`;
const UploadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-direction: column;
  cursor: pointer;
`;
const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #2c3034;
  span {
    font-size: 14px;
    font-weight: 600;
    color: #2977cc;
  }
  overflow-x: auto;
`;
const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
  padding: 10px;
  margin-top: 10px;
  border-radius: 5px;
  background: #f5f5f5;
  .file-preview {
    max-width: 100px;
    max-height: 100px;
    border-radius: 5px;
  }
`;
